import React from 'react';
import { Link as GatsbyLink } from 'gatsby';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Link } from '@printdeal/ui-library';
import {
  getStates,
  isReviewGroupLead,
  hasReviewGroup,
} from '@printdeal/business-logic';
import { Money } from '../../../Money';
import OrderLineItem from './OrderLineItem';

import {
  isPayable,
  areAllLineItemsCancelled,
  // @ts-expect-error
} from '../../../../helpers/order/Order';
import Order, { SanitizedOrder } from '../../../../types/order/Order';
import { Environment } from '../../../../helpers/env';
import PayOrderButton from '../shared/Actions/PayOrder/PayOrderButton';

interface OrderListItemWrapperProps {
  partialOrderData: Order;
  fullOrderData?: SanitizedOrder;
}

const OrderData = ({ partialOrderData, fullOrderData }: OrderListItemWrapperProps) => {
  const states = getStates(Environment);

  const {
    id,
    orderNumber,
    lineItems: partialLineItems,
    totalPrice,
    taxedPrice,
    custom: {
      fields: { referenceLabel },
    },
  } = partialOrderData;

  const {
    totalDetails,
    lineItems: fullLineItems,
  } = fullOrderData || {};

  const price = taxedPrice ? taxedPrice.totalGross : totalPrice;
  const totalDetailsPrice = (totalDetails?.pendingPayment?.centAmount || 0) > 0
    ? totalDetails?.pendingPayment
    : totalDetails?.paidAmount;

  const filteredLineItems = fullLineItems ? fullLineItems.filter(
    (item) => isReviewGroupLead(item) || !hasReviewGroup(item),
  ) : partialLineItems.filter(
    (item) => isReviewGroupLead(item) || !hasReviewGroup(item),
  );

  return (
    <>
      <div className="pd-flex pd-flex-col pd-gap-1 lg:pd-flex-row lg:pd-gap-4">
        <div className="pd-flex pd-grow pd-items-end">
          <Link
            to={id}
            as={GatsbyLink}
            variant="secondary"
            data-cy="orderLink"
            className="pd-underline"
          >
            <FormattedMessage id="orders.list.orderNumberPrefix" defaultMessage="Order" />
            {` ${orderNumber} `}
          </Link>
          {' | '}
          <span className="pd-text-dim-500">
            <FormattedDate
              value={partialOrderData.createdAt}
              year="numeric"
              month="long"
              day="2-digit"
            />
          </span>
        </div>
        <div className="pd-shrink">
          {(!areAllLineItemsCancelled(partialLineItems, states) && orderNumber) && (
            <span className="pd-flex pd-items-center pd-gap-1">
              {isPayable(partialOrderData, states, partialLineItems) ? (
                <FormattedMessage id="orders.list.notpaid" defaultMessage="Not paid yet" />
              ) : (
                <FormattedMessage id="orders.list.paid" defaultMessage="Paid" />
              )}
              {' : '}
              {!totalDetailsPrice ? (
                <span className="pd-ml-1 pd-inline pd-animate-pulse pd-font-bold">
                  <Money value={price.centAmount} currency={price.currencyCode} />
                </span>
              ) : (
                <span className="pd-ml-1 pd-inline pd-font-bold">
                  <Money value={totalDetailsPrice.centAmount} currency={totalDetailsPrice.currencyCode} />
                </span>
              )}
              {isPayable(partialOrderData, states, partialLineItems) && (
              <>
                {' | '}
                <PayOrderButton
                  order={partialOrderData as SanitizedOrder}
                  eventName="start_payment_top"
                  type="link"
                  category="order_list_interaction"
                />
              </>
              )}
            </span>
          )}
        </div>
      </div>
      {referenceLabel && (
        <div className="pd-truncate pd-text-sm pd-text-dim-600">
          {referenceLabel}
        </div>
      )}
      <ul className="pd-mt-1 pd-flex pd-flex-col pd-gap-4">
        {
          filteredLineItems.map((lineItem) => (
            <li
              key={lineItem.id}
            >
              <OrderLineItem
                lineItem={lineItem}
                order={fullOrderData || partialOrderData}
              />
            </li>
          ))
        }
      </ul>
    </>
  );
};

export default OrderData;
